import { useState } from 'react';
import ModalBase from '@/components/base/ModalBase';
import type { RfwScoreData, ScoreCategory } from '@/mocks/rfwScores';
import { scoreCategoryLabels, scoreCategoryIcons, trendConfig } from '@/mocks/rfwScores';
import { formatScoreChange } from '@/utils/rfwScoreCalculator';

interface ScoreBreakdownModalProps {
  open: boolean;
  onClose: () => void;
  score: RfwScoreData;
  entityLabel: string;
}

type BreakdownTab = 'desglose' | 'historial';

export default function ScoreBreakdownModal({ open, onClose, score, entityLabel }: ScoreBreakdownModalProps) {
  const [activeTab, setActiveTab] = useState<BreakdownTab>('desglose');
  const [historyRange, setHistoryRange] = useState<7 | 30 | 90>(7);
  const trend = trendConfig[score.trend];

  const sortedBreakdown = [...score.breakdown]
    .filter(b => b.points > 0)
    .sort((a, b) => b.points - a.points);

  const filteredHistory = score.history.slice(-historyRange);
  const maxScoreInHistory = Math.max(...filteredHistory.map(h => h.score), 1);

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={`RFW Score — ${score.entityName}`}
      subtitle={entityLabel}
      size="xl"
      headerIcon={
        <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
          <i className="ri-flashlight-fill text-accent-600 text-sm"></i>
        </div>
      }
      statusLabel={trend.emoji + ' ' + trend.label}
      statusColor={`${trend.bg} ${trend.color}`}
    >
      {/* Score Summary */}
      <div className="bg-background-50/50 rounded-xl p-4 mb-5 border border-background-200/70">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <span className="text-2xl font-extrabold text-foreground-950">{score.currentScore.toLocaleString()}</span>
            <p className="text-[10px] text-foreground-400 mt-0.5">Actual</p>
          </div>
          <div>
            <span className="text-lg font-bold text-foreground-950">{score.weeklyScore.toLocaleString()}</span>
            <p className="text-[10px] text-foreground-400 mt-0.5">Semanal</p>
          </div>
          <div>
            <span className="text-lg font-bold text-foreground-950">{score.monthlyScore.toLocaleString()}</span>
            <p className="text-[10px] text-foreground-400 mt-0.5">Mensual</p>
          </div>
          <div>
            <span className="text-lg font-bold text-foreground-950">{score.allTimeScore.toLocaleString()}</span>
            <p className="text-[10px] text-foreground-400 mt-0.5">Total</p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-4 mt-3 text-sm">
          <span className="text-foreground-700 font-semibold">Ranking #{score.rankingPosition}</span>
          <span className="text-foreground-300">|</span>
          <span className={`font-semibold ${score.scoreChange > 0 ? 'text-emerald-600' : score.scoreChange < 0 ? 'text-red-500' : 'text-foreground-400'}`}>
            {formatScoreChange(score.scoreChange)}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full mb-5">
        <button
          onClick={() => setActiveTab('desglose')}
          className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'desglose'
              ? 'bg-background-50 text-foreground-950 shadow-sm'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-pie-chart-line mr-1.5"></i>Desglose
        </button>
        <button
          onClick={() => setActiveTab('historial')}
          className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'historial'
              ? 'bg-background-50 text-foreground-950 shadow-sm'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-history-line mr-1.5"></i>Historial
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'desglose' ? (
        <div className="space-y-1.5">
          {/* Total bar at top */}
          <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-accent-50 border border-accent-100 mb-3">
            <span className="text-xs font-bold text-accent-800">Total Score</span>
            <span className="text-sm font-extrabold text-accent-800">{score.currentScore.toLocaleString()} pts</span>
          </div>

          {sortedBreakdown.map((item, idx) => {
            const pct = (item.points / score.currentScore) * 100;
            return (
              <div key={item.category} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-background-50 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                  <i className={`${scoreCategoryIcons[item.category] || 'ri-question-line'} text-foreground-500 text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-foreground-800">{scoreCategoryLabels[item.category] || item.category}</span>
                    <span className="text-xs font-bold text-foreground-950">{item.points.toLocaleString()} pts</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-background-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-accent-500 transition-all"
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-[10px] text-foreground-400 mt-0.5">{item.count.toLocaleString()} {item.count === 1 ? 'acción' : 'acciones'} · {pct.toFixed(1)}%</p>
                </div>
              </div>
            );
          })}

          {sortedBreakdown.length === 0 && (
            <div className="py-8 text-center text-foreground-400 text-sm">
              <i className="ri-inbox-line text-2xl block mb-2"></i>
              Sin desglose disponible
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Range selector */}
          <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full mb-4 w-fit">
            {([7, 30, 90] as const).map(range => (
              <button
                key={range}
                onClick={() => setHistoryRange(range)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  historyRange === range
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {range}d
              </button>
            ))}
          </div>

          {/* Mini Bar Chart */}
          <div className="h-32 flex items-end gap-1 mb-4">
            {filteredHistory.map((entry, idx) => {
              const heightPct = (entry.score / maxScoreInHistory) * 100;
              const color = entry.change > 0 ? 'bg-emerald-400' : entry.change < 0 ? 'bg-red-400' : 'bg-foreground-300';
              return (
                <div key={idx} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div
                    className={`w-full rounded-t-md ${color} transition-all hover:brightness-90 min-h-[4px]`}
                    style={{ height: `${Math.max(heightPct, 4)}%` }}
                  ></div>
                  <span className="text-[8px] text-foreground-400">
                    {new Date(entry.date).getDate()}
                  </span>
                </div>
              );
            })}
          </div>

          {/* History list */}
          <div className="space-y-1">
            {[...filteredHistory].reverse().map((entry, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-background-50 transition-colors text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-foreground-400">
                    {new Date(entry.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                  <span className="font-bold text-foreground-950">{entry.score.toLocaleString()}</span>
                </div>
                <span className={`text-xs font-semibold ${entry.change > 0 ? 'text-emerald-600' : entry.change < 0 ? 'text-red-500' : 'text-foreground-400'}`}>
                  {entry.change > 0 ? '+' : ''}{entry.change}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ModalBase>
  );
}